"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LockError = exports.handleRejection = exports.allToResolveOrReject = void 0;
exports._synchronized_3 = _synchronized_3;
exports.synchronized = synchronized;
/**
 * The difference between this and `Promise.all` is that `Promise.all` rejects
 * when any promise rejects, and this resolves when all promises resolve _or_
 * reject.
 *
 * This function returns an array of results, each of which is either a success
 * result containing the resolved promise, or an error result containing the
 * value that was passed to `reject()`.
 *
 * This function itself never throws or rejects.
 */
const allToResolveOrReject = (promises) => __awaiter(void 0, void 0, void 0, function* () {
    // Attach a dummy rejection handler to each promise as soon as possible, to
    // try to prevent them from rejecting in the background without a handler, as
    // that can cause issues, depending on the runtime environment.
    //
    promises.forEach(exports.handleRejection); // try to comment this out & run the tests
    const results = [];
    for (const promise of promises) {
        try {
            const value = yield promise;
            results.push({ isSuccess: true, value });
        }
        catch (error) {
            results.push({ isSuccess: false, error });
        }
    }
    return results;
});
exports.allToResolveOrReject = allToResolveOrReject;
/**
 * Attach a dummy rejection handler to a promise and return the original
 * promise. Used to prevent unhandled rejections in the background.
 *
 * To use this, wrap any `async` or `Promise`-returning function with this
 * function and use it as you otherwise would.
 *
 * Calling this multiple times for the same promise should have no ill effect.
 *
 * @example
 * // Before
 * await fetch('https://hevy.com/blabla');
 *
 * // After
 * await handleRejection(fetch('https://hevy.com/blabla'));
 */
const handleRejection = (p) => (p.catch(() => { }), p);
exports.handleRejection = handleRejection;
/**
 * Do not use this directly. Use the alias provided by the parent project.
 */
function _synchronized_3(arg0, arg1) {
    if (typeof arg1 === 'object' &&
        arg1 !== null &&
        'name' in arg1 &&
        'access' in arg1) {
        return synchronizedStage3DecoratorFactory(arg0, arg1, true);
    }
    else {
        return function (value, context) {
            return synchronizedStage3DecoratorFactory(value, context, arg0, arg1);
        };
    }
}
function synchronized(arg0, arg1, arg2) {
    if (arguments.length === 1) {
        return function (target, propertyKey, descriptor) {
            return synchronizedLegacyDecoratorFactory(target, propertyKey, descriptor, arg0);
        };
    }
    else if (arguments.length === 2) {
        return function (target, propertyKey, descriptor) {
            return synchronizedLegacyDecoratorFactory(target, propertyKey, descriptor, arg0, arg1);
        };
    }
    else {
        return synchronizedLegacyDecoratorFactory(arg0, arg1, arg2, true);
    }
}
const $queue = Symbol();
class LockError extends Error {
    constructor(...args) {
        super(...args);
    }
}
exports.LockError = LockError;
class $LockError extends LockError {
    constructor({ message: baseMessage, propertyKey, lockId, }) {
        const formattedLockId = lockId !== undefined ? `"${String(lockId)}"` : '(anonymous)';
        const message = `Cannot invoke "${String(propertyKey)}" (locked by ${formattedLockId}): ${baseMessage}`;
        super(message);
        this.name = 'LockError';
        this.message = message;
        this.propertyKey = propertyKey;
        this.lockId = lockId;
    }
}
function synchronizedStage3DecoratorFactory(origFn, context, wait, id) {
    const lockId = arguments.length === 4 ? id : Symbol();
    const propertyKey = context.name;
    return synchronizedDecoratorFactory({
        id,
        lockId,
        origFn,
        propertyKey,
        wait,
    });
}
function synchronizedLegacyDecoratorFactory(_target, propertyKey, descriptor, wait, id) {
    const origFn = descriptor.value;
    const lockId = arguments.length === 5 ? id : Symbol();
    descriptor.value = synchronizedDecoratorFactory({
        id,
        lockId,
        origFn,
        propertyKey,
        wait,
    });
}
function synchronizedDecoratorFactory({ id, lockId, origFn, propertyKey, wait, }) {
    return function (...args) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            var _c;
            const queue = ((_b = (_c = ((_a = this[$queue]) !== null && _a !== void 0 ? _a : (this[$queue] = {})))[lockId]) !== null && _b !== void 0 ? _b : (_c[lockId] = []));
            if (queue.length > 0 && !wait) {
                throw new $LockError({
                    message: 'Operation is already in progress',
                    propertyKey,
                    lockId: id,
                });
            }
            let done;
            const thisCall = new Promise((resolve) => {
                done = resolve;
            });
            const waiting = [...queue];
            queue.push(thisCall);
            yield (0, exports.allToResolveOrReject)(waiting);
            try {
                return yield origFn.apply(this, args);
            }
            finally {
                done();
                if (queue.shift() !== thisCall) {
                    // eslint-disable-next-line no-unsafe-finally
                    throw new $LockError({
                        message: 'Assertion failed: @synchronized queue is mangled',
                        propertyKey,
                        lockId: id,
                    });
                }
            }
        });
    };
}
